import React from "react";

import UploadIcon from "@uui/Upload01";

import type { ReportSource } from "@app/analytics/MeetingReports/types";
import * as TopicUtils from "@app/common/utils/topicUtils";

import IconBackground, { IconBackgroundProps } from "@rui/IconBackground";
import LogomarkWithGradient from "@rui/LogomarkWithGradient";
import { Img } from "@rui/styles";

interface SourceIconProps extends Omit<IconBackgroundProps, "height"> {
  size: "sm" | "md" | "lg";
  source: ReportSource;
}

const BG_COLOR_FOR_SOURCE: Record<
  SourceIconProps["source"],
  React.CSSProperties["backgroundColor"] | undefined
> = {
  // meeting sources
  zoom: "#E3EBFB",
  meet: "#E3F9E9",
  teams: "#E8E9FA",
  webex: "#DBF3F9",
  upload: "shades.neutral.50",
  // readout sources
  slack_ingest: "#FDF6E6",
  microsoft_graph_email: "#D6F4FC",
  microsoft_graph_teams_chat_ingest: "#E8E9FA",
  google_email: "#E3F9E9",
  multi: undefined,
  // other
  unknown: "#EDEDFA",
  browser: undefined,
};

const LOGOMARK_SX = {
  height: "16px",
  width: "16px",
  padding: "2.5px",
  borderRadius: "6px",
  "& svg": {
    height: "100%",
    width: "100%",
  },
};

const HEIGHT_BY_SIZE: { [key in SourceIconProps["size"]]: number } = {
  sm: 16,
  md: 24,
  lg: 32,
};

const IMG_WIDTH_BY_SIZE: { [key in SourceIconProps["size"]]: number } = {
  sm: 10,
  md: 16,
  lg: 16,
};

const SourceIcon = ({ size, source, ...props }: SourceIconProps) => {
  const imgSrc =
    source === "upload" || source === "browser"
      ? undefined
      : // topic providers include all meeting platforms as well as topic-specific providers
        TopicUtils.getProviderLogoSrc(source);
  const bgcolor = BG_COLOR_FOR_SOURCE[source];

  return (
    <IconBackground
      {...props}
      height={HEIGHT_BY_SIZE[size]}
      bgcolor={bgcolor}
      borderRadius={size === "sm" ? "4px" : "8px"}
      border="1px solid"
      borderColor="transparent.black.4"
    >
      {imgSrc ? (
        <Img src={imgSrc} width={IMG_WIDTH_BY_SIZE[size]} />
      ) : source === "upload" ? (
        <UploadIcon sx={{ color: "shades.neutral.500" }} />
      ) : source === "unknown" ? (
        <LogomarkWithGradient sx={LOGOMARK_SX} />
      ) : null}
    </IconBackground>
  );
};

export default SourceIcon;
